import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Dollar } from "@faire/web--source/slate/icons/foundation/Dollar";
import React from "react";

import { SelfServeNodes } from "../../types";
import { usePaymentPayoutsArticles } from "../instantAnswers/articles/usePaymentPayoutsArticles";
import { usePaymentUpdatePayoutsArticles } from "../instantAnswers/articles/usePaymentUpdatePayoutsArticles";
import { adyenIssue } from "../instantAnswers/payments-adyenIssue";
import { discrepancyInPayoutTotal } from "../instantAnswers/payments-discrepancyInPayoutTotal";
import { payoutCalculated } from "../instantAnswers/payments-payoutCalculated";
import { PayoutNotReceived } from "../instantAnswers/payments-payoutNotReceived";
import { setUpPayouts } from "../instantAnswers/payments-setUpPayouts";
import { taxForm1099 } from "../instantAnswers/payments-taxForm1099";
import { updatePayouts } from "../instantAnswers/payments-updatePayouts";

export const usePaymentsBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  const updatePayoutsArticles = usePaymentUpdatePayoutsArticles();
  const payoutsArticles = usePaymentPayoutsArticles();
  return {
    Payments: {
      id: "Payments",
      label: strictLocalize({
        defaultMessage: "Payments",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Dollar fontSize="24px" color="faireBlack" />,
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Payment issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
      children: [
        "SetUpPayouts",
        "AdyenIssue",
        "PayoutNotReceived",
        "HowPayoutsCalculated",
        "DiscrepancyInPayoutTotal",
        "UpdatePayoutTerms",
        "TaxForm1099",
      ],
    },
    SetUpPayouts: {
      id: "SetUpPayouts",
      label: strictLocalize({
        defaultMessage: "Set up payouts",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: payoutsArticles,
      instantAnswer: setUpPayouts(strictLocalize),
      level: 2,
    },
    AdyenIssue: {
      id: "AdyenIssue",
      label: strictLocalize({
        defaultMessage: "Adyen issue",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: adyenIssue(strictLocalize),
      articles: [
        articles.InvoicesAndBilling.PAYOUT_EUROPEAN,
        articles.InvoicesAndBilling.PAYOUT_AUSTRALIAN,
        articles.InvoicesAndBilling.TROUBLESHOOTING_WITH_ADYEN,
      ],
      level: 2,
    },
    PayoutNotReceived: {
      id: "PayoutNotReceived",
      label: strictLocalize({
        defaultMessage: "Payout not received",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.InvoicesAndBilling.ISSUE_WITH_MY_ORDER_PAYOUT],
      instantAnswer: PayoutNotReceived(strictLocalize),
      level: 2,
    },
    AboutPayoutCalculations: {
      id: "AboutPayoutCalculations",
      label: strictLocalize({
        defaultMessage: "About payout calculations",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: payoutCalculated(strictLocalize),
      articles: [
        articles.InvoicesAndBilling.NON_NAM_SELL_ON_FAIRE,
        articles.InvoicesAndBilling.NAM_SELL_ON_FAIRE,
      ],
      level: 2,
    },
    DiscrepancyInPayoutTotal: {
      id: "DiscrepancyInPayoutTotal",
      label: strictLocalize({
        defaultMessage: "Discrepancy in payout total",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.InvoicesAndBilling.NON_NAM_SELL_ON_FAIRE,
        articles.InvoicesAndBilling.NAM_SELL_ON_FAIRE,
      ],
      instantAnswer: discrepancyInPayoutTotal(strictLocalize),
      level: 2,
    },
    UpdatePayoutTerms: {
      id: "UpdatePayoutTerms",
      label: strictLocalize({
        defaultMessage: "Update payout terms",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: updatePayoutsArticles,
      instantAnswer: updatePayouts(strictLocalize),
      level: 2,
    },
    DownloadMyTaxForms: {
      id: "DownloadMyTaxForms",
      label: strictLocalize({
        defaultMessage: "Download my tax forms",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: taxForm1099(strictLocalize),
      articles: [articles.InvoicesAndBilling.TAX_FORM_1099],
      level: 2,
    },
  };
};
